// this class is based off of react-bootstrap@0.29.5 InputBase that was removed in @0.30.0
import React from 'react';
import PropTypes from 'prop-types';
import FormGroup from 'react-bootstrap/FormGroup';
import { FAIcon } from 'reactifi';

export default class InputBase extends React.Component {
  static propTypes = {
    type: PropTypes.string,
    label: PropTypes.node,
    help: PropTypes.node,
    addonBefore: PropTypes.node,
    addonAfter: PropTypes.node,
    buttonBefore: PropTypes.node,
    buttonAfter: PropTypes.node,
    bsSize: PropTypes.oneOf(['small', 'medium', 'large']),
    bsStyle: PropTypes.oneOf(['success', 'warning', 'error']),
    hasFeedback: PropTypes.bool,
    feedbackIcon: PropTypes.node,
    id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    groupClassName: PropTypes.string,
    wrapperClassName: PropTypes.string,
    labelClassName: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
    multiple: PropTypes.bool,
    disabled: PropTypes.bool,
    value: PropTypes.any
  };

  static defaultProps = {
    disabled: false,
    hasFeedback: false,
    multiple: false
  };

  getInputDOMNode = () => {
    return this.refs.input;
  };

  getValue = () => {
    if (this.props.type === 'static') {
      return this.props.value;
    } else if (this.props.type) {
      if (this.props.type === 'select' && this.props.multiple) {
        return this.getSelectedOptions();
      }
      return this.getInputDOMNode().value;
    }
    throw new Error('Cannot use getValue without specifying input type.');
  };

  getChecked = () => {
    return this.getInputDOMNode().checked;
  };

  getSelectedOptions = () => {
    let values = [];

    Array.prototype.forEach.call(
      this.getInputDOMNode().getElementsByTagName('option'),
      (option) => {
        if (option.selected) {
          let value = option.getAttribute('value') || option.innerHtml;
          values.push(value);
        }
      });

    return values;
  };

  isCheckboxOrRadio = () => {
    return this.props.type === 'checkbox' || this.props.type === 'radio';
  };

  isFile = () => {
    return this.props.type === 'file';
  };

  renderInputGroup = (children) => {
    let addonBefore = this.props.addonBefore ? (
      <span className="input-group-addon" key="addonBefore">
        {this.props.addonBefore}
      </span>
    ) : null;

    let addonAfter = this.props.addonAfter ? (
      <span className="input-group-addon" key="addonAfter">
        {this.props.addonAfter}
      </span>
    ) : null;

    let buttonBefore = this.props.buttonBefore ? (
      <span className="input-group-btn">
        {this.props.buttonBefore}
      </span>
    ) : null;

    let buttonAfter = this.props.buttonAfter ? (
      <span className="input-group-btn">
        {this.props.buttonAfter}
      </span>
    ) : null;

    let inputGroupClassName;
    switch (this.props.bsSize) {
    case 'small': inputGroupClassName = 'input-group-sm'; break;
    case 'large': inputGroupClassName = 'input-group-lg'; break;
    default:
    }

    return addonBefore || addonAfter || buttonBefore || buttonAfter ? (
      <div className={`${inputGroupClassName} input-group`} key="input-group">
        {addonBefore}
        {buttonBefore}
        {children}
        {addonAfter}
        {buttonAfter}
      </div>
    ) : children;
  };

  renderIcon = () => {
    if (this.props.hasFeedback) {
      if (this.props.feedbackIcon) {
        return React.cloneElement(this.props.feedbackIcon, { formControlFeedback: true });
      }

      switch (this.props.bsStyle) {
      case 'success': return  <FAIcon icon="circle-check" />
      case 'warning': return  <FAIcon icon="triangle-exclamation" />
      case 'error': return <FAIcon icon="circle-exclamation" />
      default: return <span className="form-control-feedback" key="icon" />;
      }
    } else {
      return null;
    }
  };

  renderHelp = () => {
    return this.props.help ? (
      <span className="form-text" key="help">
        {this.props.help}
      </span>
    ) : null;
  };

  renderCheckboxAndRadioWrapper = (children) => {
    let className = "";
    switch (this.props.type) {
    case "checkbox": className = "checkbox"; break;
    case "radio": className = "radio"; break;
    default:
    }

    return (
      <div className={className} key="checkboxRadioWrapper">
        {children}
      </div>
    );
  }

  renderWrapper = (children) => {
    return this.props.wrapperClassName ? (
      <div className={this.props.wrapperClassName} key="wrapper">
        {children}
      </div>
    ) : children;
  };

  renderLabel = (children) => {
    return this.props.label ? (
      <label htmlFor={this.props.id} className={`${!this.isCheckboxOrRadio() ? 'form-label ' : ''}${this.props.labelClassName}`} key="label">
        {children}
        {this.props.label}
      </label>
    ) : children;
  };

  renderInput = () => {
    if (!this.props.type) {
      return this.props.children;
    }
    const { className } = this.props;
    switch (this.props.type) {
    case 'select':
      return (
        <select {...this.props} className={`${className} form-control`} ref="input" key="input">
          {this.props.children}
        </select>
      );
    case 'textarea':
      return <textarea {...this.props} className={`${className} form-control`} ref="input" key="input" />;
    case 'static':
      return (
        <p {...this.props} className={`${className} form-control-plaintext`} ref="input" key="input">
          {this.props.value}
        </p>
      );
    default: {
      const formClassName = this.isCheckboxOrRadio() || this.isFile() ? '' : 'form-control';
      return <input {...this.props} className={`${className} ${formClassName}`} ref="input" key="input" />;
    }
    }
  };

  renderFormGroup = (children) => {
    return <FormGroup {...this.props}>{children}</FormGroup>;
  }

  renderChildren = () => {
    return !this.isCheckboxOrRadio() ? [
      this.renderLabel(),
      this.renderWrapper([
        this.renderInputGroup(
          this.renderInput()
        ),
        this.renderIcon(),
        this.renderHelp()
      ])
    ] : this.renderWrapper([
      this.renderCheckboxAndRadioWrapper(
        this.renderLabel(
          this.renderInput(),
          this.renderInputHelper()
        )
      ),
      this.renderHelp()
    ]);
  };

  render() {
    let children = this.renderChildren();
    return this.renderFormGroup(children);
  }
}
